import { useState, useEffect } from 'react';
import type { Address, AddressRecord } from '../types';
import { US_STATES, CANADIAN_PROVINCES } from '../constants/regions';
import { GIFT_OPTIONS, TSHIRT_SIZES } from '../constants/giftOptions';

interface AddressFormProps {
  title: string;
  address: Address;
  onChange: (address: Address) => void;
  savedAddresses?: AddressRecord[];
  showSavedDropdown?: boolean;
  /** View-only: inputs become readOnly, selects disabled, but stay focusable. */
  readOnly?: boolean;
  /** Fully disable every control (e.g. while a save is in flight). */
  disabled?: boolean;
  /** Override the slug used for field ids; needed when several forms render at
   *  once (e.g. one per table row). Defaults to a slug of `title`. */
  idPrefix?: string;
  /** Render just the fields, without the `.form-section` wrapper + `<h2>` —
   *  for embedding inside a host that supplies its own heading/container. */
  hideHeader?: boolean;
}

function AddressForm({
  title,
  address,
  onChange,
  savedAddresses = [],
  showSavedDropdown = false,
  readOnly = false,
  disabled = false,
  idPrefix,
  hideHeader = false,
}: AddressFormProps) {
  const [selectedSavedId, setSelectedSavedId] = useState('');
  const fieldPrefix = idPrefix ?? title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  const regions = address.country === 'CA' ? CANADIAN_PROVINCES : US_STATES;

  useEffect(() => {
    // Reset saved selection when address changes externally
    if (!address.name && !address.street1) {
      setSelectedSavedId('');
    }
  }, [address]);

  const handleChange = (field: keyof Address, value: string | boolean) => {
    onChange({
      ...address,
      [field]: value,
      ...(field === 'gift' && value !== 'Tshirt' ? { tshirt_size: '' } : {}),
      ...(field === 'country' && value !== address.country ? { state: '' } : {}),
    });
  };

  const handleSavedSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    setSelectedSavedId(id);

    if (id === '') {
      // Clear form
      onChange({
        name: '',
        company: '',
        street1: '',
        street2: '',
        city: '',
        state: '',
        zip: '',
        email: '',
        phone: '',
        gift: '',
        country: 'US',
      });
      return;
    }

    const saved = savedAddresses.find((a) => a.id === id);
    if (saved) {
      onChange({
        name: saved.name || '',
        company: saved.company || '',
        street1: saved.street1 || '',
        street2: saved.street2 || '',
        city: saved.city || '',
        state: saved.state || '',
        zip: saved.zip || '',
        email: saved.email || '',
        phone: saved.phone || '',
        country: saved.country || 'US',
        gift: saved.gift || '',
        product_type: saved.product_type,
        tshirt_size: saved.tshirt_size,
      });
    }
  };

  const body = (
    <fieldset disabled={disabled} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      {showSavedDropdown && savedAddresses.length > 0 && (
        <div className="form-group">
          <label htmlFor={`${fieldPrefix}-saved-address`}>Use Saved Address</label>
          <select
            id={`${fieldPrefix}-saved-address`}
            value={selectedSavedId}
            onChange={handleSavedSelect}
            disabled={readOnly}
          >
            <option value="">-- Enter new address --</option>
            {savedAddresses.map((addr) => (
              <option key={addr.id} value={addr.id}>
                {addr.name || addr.company} - {addr.city}, {addr.state}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="form-group">
        <label htmlFor={`${fieldPrefix}-name`}>Name *</label>
        <input
          id={`${fieldPrefix}-name`}
          type="text"
          value={address.name || ''}
          onChange={(e) => handleChange('name', e.target.value)}
          placeholder="Full name"
          required
          readOnly={readOnly}
        />
      </div>

      <div className="form-group">
        <label htmlFor={`${fieldPrefix}-company`}>Company</label>
        <input
          id={`${fieldPrefix}-company`}
          type="text"
          value={address.company || ''}
          onChange={(e) => handleChange('company', e.target.value)}
          placeholder="Company name (optional)"
          readOnly={readOnly}
        />
      </div>

      <div className="form-group">
        <label htmlFor={`${fieldPrefix}-street1`}>Street Address *</label>
        <input
          id={`${fieldPrefix}-street1`}
          type="text"
          value={address.street1 || ''}
          onChange={(e) => handleChange('street1', e.target.value)}
          placeholder="Street address"
          required
          readOnly={readOnly}
        />
      </div>

      <div className="form-group">
        <label htmlFor={`${fieldPrefix}-street2`}>Apt / Suite / Unit</label>
        <input
          id={`${fieldPrefix}-street2`}
          type="text"
          value={address.street2 || ''}
          onChange={(e) => handleChange('street2', e.target.value)}
          placeholder="Apartment, suite, unit, etc. (optional)"
          readOnly={readOnly}
        />
      </div>

      <div className="form-group">
        <label htmlFor={`${fieldPrefix}-country`}>Country *</label>
        <select
          id={`${fieldPrefix}-country`}
          value={address.country || 'US'}
          onChange={(e) => handleChange('country', e.target.value)}
          required
          disabled={readOnly}
        >
          <option value="US">United States</option>
          <option value="CA">Canada</option>
        </select>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor={`${fieldPrefix}-city`}>City *</label>
          <input
            id={`${fieldPrefix}-city`}
            type="text"
            value={address.city || ''}
            onChange={(e) => handleChange('city', e.target.value)}
            placeholder="City"
            required
            readOnly={readOnly}
          />
        </div>

        <div className="form-group">
          <label htmlFor={`${fieldPrefix}-state`}>{address.country === 'CA' ? 'Province' : 'State'} *</label>
          <select
            id={`${fieldPrefix}-state`}
            value={address.state || ''}
            onChange={(e) => handleChange('state', e.target.value)}
            required
            disabled={readOnly}
          >
            {regions.map((state) => (
              <option key={state.value} value={state.value}>
                {state.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor={`${fieldPrefix}-zip`}>{address.country === 'CA' ? 'Postal Code' : 'ZIP Code'} *</label>
          <input
            id={`${fieldPrefix}-zip`}
            type="text"
            value={address.zip || ''}
            onChange={(e) => handleChange('zip', e.target.value.toUpperCase())}
            placeholder="ZIP code"
            required
            maxLength={10}
            readOnly={readOnly}
          />
        </div>

        <div className="form-group">
          <label htmlFor={`${fieldPrefix}-phone`}>Phone</label>
          <input
            id={`${fieldPrefix}-phone`}
            type="tel"
            value={address.phone || ''}
            onChange={(e) => handleChange('phone', e.target.value)}
            placeholder="Phone number"
            readOnly={readOnly}
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor={`${fieldPrefix}-email`}>Email</label>
        <input
          id={`${fieldPrefix}-email`}
          type="email"
          value={address.email || ''}
          onChange={(e) => handleChange('email', e.target.value)}
          placeholder="Email address (for tracking notifications)"
          readOnly={readOnly}
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor={`${fieldPrefix}-gift`}>Gift</label>
          <select
            id={`${fieldPrefix}-gift`}
            value={address.gift || ''}
            onChange={(e) => handleChange('gift', e.target.value)}
            disabled={readOnly}
          >
            {GIFT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        {address.gift === 'Tshirt' && (
          <div className="form-group">
            <label htmlFor={`${fieldPrefix}-tshirt-size`}>T-Shirt Size *</label>
            <select
              id={`${fieldPrefix}-tshirt-size`}
              value={address.tshirt_size || ''}
              onChange={(e) => handleChange('tshirt_size', e.target.value)}
              disabled={readOnly}
              required
            >
              {TSHIRT_SIZES.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>
    </fieldset>
  );

  if (hideHeader) return body;

  return (
    <section className="form-section" aria-labelledby={`${fieldPrefix}-heading`}>
      <h2 id={`${fieldPrefix}-heading`}>{title}</h2>
      {body}
    </section>
  );
}

export default AddressForm;
